import {
  PortalArticleBlock,
  PortalArticleSection,
  PortalSummaryTopic,
} from '../../../models/PortalArtigoModel'
import {
  ensureSummaryHref,
  getSectionSummaryAnchor,
  normalizePortalAnchorValue,
} from './anchor-utils'

type SummaryBlock = Extract<PortalArticleBlock, { type: 'summary' }>

export function syncBlockAnchorChanges(
  blocks: PortalArticleBlock[],
  previousBlock: PortalArticleBlock | undefined,
  nextBlock: PortalArticleBlock
) {
  let synced = blocks
  collectSectionAnchorChanges(previousBlock, nextBlock).forEach(({ previousAnchor, nextAnchor }) => {
    synced = updateSummaryHrefReferences(synced, previousAnchor, nextAnchor)
  })
  if (previousBlock?.type === 'summary' && nextBlock.type === 'summary') {
    collectSummaryHrefChanges(previousBlock.topics, nextBlock.topics).forEach((change) => {
      synced = updateFirstSectionAnchorReference(synced, change.previousAnchor, change.nextAnchor)
      synced = updateSummaryHrefReferences(synced, change.previousAnchor, change.nextAnchor)
    })
  }
  return synced
}

function collectSectionAnchorChanges(
  previousBlock: PortalArticleBlock | undefined,
  nextBlock: PortalArticleBlock
) {
  const changes: Array<{ previousAnchor: string; nextAnchor: string }> = []
  if (previousBlock?.type === 'intro' && nextBlock.type === 'intro') {
    addChange(changes, previousBlock.section, nextBlock.section)
  }
  if (previousBlock?.type === 'topic' && nextBlock.type === 'topic') {
    nextBlock.items.forEach((nextItem) => {
      if (nextItem.type !== 'section') return
      const previousItem = previousBlock.items.find(
        (item) => item.type === 'section' && item.id && item.id === nextItem.id
      )
      if (previousItem?.type === 'section') addChange(changes, previousItem.section, nextItem.section)
    })
  }
  return changes
}

function addChange(
  changes: Array<{ previousAnchor: string; nextAnchor: string }>,
  previousSection: PortalArticleSection,
  nextSection: PortalArticleSection
) {
  const previousAnchor = normalizePortalAnchorValue(getSectionSummaryAnchor(previousSection))
  const nextAnchor = normalizePortalAnchorValue(getSectionSummaryAnchor(nextSection))
  if (previousAnchor && nextAnchor && previousAnchor !== nextAnchor) {
    changes.push({ previousAnchor, nextAnchor })
  }
}

function collectSummaryHrefChanges(previousTopics: PortalSummaryTopic[], nextTopics: PortalSummaryTopic[]) {
  const changes: Array<{ previousAnchor: string; nextAnchor: string }> = []
  nextTopics.forEach((nextTopic, index) => {
    addHrefChange(changes, previousTopics[index]?.href, nextTopic.href)
    nextTopic.subtopics?.forEach((nextSubtopic, subtopicIndex) => {
      addHrefChange(changes, previousTopics[index]?.subtopics?.[subtopicIndex]?.href, nextSubtopic.href)
    })
  })
  return changes
}

function addHrefChange(
  changes: Array<{ previousAnchor: string; nextAnchor: string }>,
  previousHref?: string,
  nextHref?: string
) {
  const previousAnchor = normalizePortalAnchorValue(previousHref)
  const nextAnchor = normalizePortalAnchorValue(nextHref)
  if (previousAnchor && nextAnchor && previousAnchor !== nextAnchor) {
    changes.push({ previousAnchor, nextAnchor })
  }
}

function updateSummaryHrefReferences(
  blocks: PortalArticleBlock[],
  previousAnchor: string,
  nextAnchor: string
) {
  const matches = blocks
    .filter((block): block is SummaryBlock => block.type === 'summary')
    .flatMap((block) =>
      block.topics.flatMap((topic) => [topic.href, ...(topic.subtopics || []).map((item) => item.href)])
    )
    .filter((href) => normalizePortalAnchorValue(href) === previousAnchor)
  if (matches.length !== 1) return blocks

  return blocks.map((block) => {
    if (block.type !== 'summary') return block
    return {
      ...block,
      topics: block.topics.map((topic) => ({
        ...topic,
        href: replaceSummaryHref(topic.href, previousAnchor, nextAnchor),
        subtopics: topic.subtopics?.map((subtopic) => ({
          ...subtopic,
          href: replaceSummaryHref(subtopic.href, previousAnchor, nextAnchor),
        })),
      })),
    }
  })
}

function replaceSummaryHref(href: string, previousAnchor: string, nextAnchor: string) {
  return normalizePortalAnchorValue(href) === previousAnchor ? ensureSummaryHref(nextAnchor) : href
}

function updateFirstSectionAnchorReference(
  blocks: PortalArticleBlock[],
  previousAnchor: string,
  nextAnchor: string
) {
  let matches = 0
  blocks.forEach((block) => {
    if (block.type === 'intro' && normalizePortalAnchorValue(getSectionSummaryAnchor(block.section)) === previousAnchor) matches += 1
    if (block.type === 'topic') {
      block.items.forEach((item) => {
        if (item.type === 'section' && normalizePortalAnchorValue(getSectionSummaryAnchor(item.section)) === previousAnchor) matches += 1
      })
    }
  })
  if (matches !== 1) return blocks

  return blocks.map((block) => {
    if (block.type === 'intro' && normalizePortalAnchorValue(getSectionSummaryAnchor(block.section)) === previousAnchor) {
      return { ...block, section: { ...block.section, anchorId: nextAnchor } }
    }
    if (block.type !== 'topic') return block
    return {
      ...block,
      items: block.items.map((item) =>
        item.type === 'section' && normalizePortalAnchorValue(getSectionSummaryAnchor(item.section)) === previousAnchor
          ? { ...item, section: { ...item.section, anchorId: nextAnchor } }
          : item
      ),
    }
  })
}
